import React, { useState } from 'react';
import { Box, Text, PopoverMenu, TextButton } from '@wix/design-system';
import { ChevronDown, ChevronUp } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/fe-essentials-standalone';
import clsx from 'clsx';
import { communityNavSubHeaderDataHooks } from '../CommunityNavSubHeader.dataHooks';
import {
  COMMUNITY_NAV_DROPDOWN_OPTIONS,
  DROPDOWN_IDS,
  DropdownId,
} from '../navSubHeaderUtils';
import DiscordButton from '../DiscordButton';
import useCommunityNavSubHeaderBi, {
  ACTION_NAMES,
} from '../CommunityNavSubHeaderMobile/useCommunityNavSubHeaderBi';

import s from '../CommunityNavSubHeader.module.scss';

interface Props {
  className?: string;
}

const CommunityNavSubHeaderDesktop = ({ className }: Props) => {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const { reportElementsClick, reportSubHeaderClick } =
    useCommunityNavSubHeaderBi();

  const renderDropdown = (id: DropdownId) => {
    const labelKey = `marketplace.community-nav-sub-header.${id}`;
    const isOpen = openDropdown === id;
    const options = COMMUNITY_NAV_DROPDOWN_OPTIONS[id];

    return (
      <PopoverMenu
        key={id}
        appendTo="viewport"
        placement="bottom-start"
        dataHook={communityNavSubHeaderDataHooks.dropdown({ id })}
        onHide={() => setOpenDropdown(null)}
        onShow={() => setOpenDropdown(id)}
        showArrow={false}
        minWidth="220px"
        textSize="small"
        triggerElement={
          <TextButton
            as="button"
            skin="dark"
            size="medium"
            weight={isOpen ? 'bold' : 'thin'}
            onClick={() => {
              reportElementsClick({
                elementTitle: t(`marketplace.community-nav-sub-header.${id}`),
                actionName: ACTION_NAMES.OpenMenu,
              });
            }}
            suffixIcon={isOpen ? <ChevronUp /> : <ChevronDown />}
            dataHook={communityNavSubHeaderDataHooks.trigger({ id })}
          >
            {t(labelKey)}
          </TextButton>
        }
      >
        {options.map(({ hrefKey, labelKey, linkType, id, clickType }) => {
          const targetHref = t(hrefKey);

          return (
            <PopoverMenu.MenuItem
              key={hrefKey}
              skin="dark"
              text={
                <Text
                  tagName="a"
                  href={targetHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  weight="thin"
                  onClick={() => {
                    reportSubHeaderClick(id, {
                      elementTitle: t(labelKey),
                      actionName:
                        linkType === 'external'
                          ? ACTION_NAMES.ExternalLink
                          : ACTION_NAMES.PageLink,
                      value: targetHref,
                      clickType: clickType,
                    });
                  }}
                >
                  {t(labelKey)}
                </Text>
              }
            />
          );
        })}
      </PopoverMenu>
    );
  };

  return (
    <header
      data-hook={communityNavSubHeaderDataHooks.root()}
      style={{ overflow: 'hidden' }}
      className={clsx(className, s.headerContainer)}
    >
      <Box
        direction="horizontal"
        align="space-between"
        verticalAlign="middle"
        padding="12px SP6"
        dataHook={communityNavSubHeaderDataHooks.root()}
      >
        <Text
          size="medium"
          weight="normal"
          dataHook={communityNavSubHeaderDataHooks.title()}
        >
          {t('marketplace.community-nav-sub-header.title')}
        </Text>
        <Box direction="horizontal" verticalAlign="middle" gap="30px">
          {DROPDOWN_IDS.map(renderDropdown)}
          <DiscordButton />
        </Box>
      </Box>
    </header>
  );
};

export default CommunityNavSubHeaderDesktop;
